import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Cookies from 'js-cookie'
import API_BASE_URL from '../../../../config/api'
import axios from 'axios'
import Swal from 'sweetalert2'
import Topbar from '../../mini-comp/Topbar'
import Sidebar from '../../mini-comp/Sidebar'
import {
    RiAddLine,
    RiEdit2Line,
    RiDeleteBinLine,
    RiMoneyDollarCircleLine,
    RiTimeLine,
    RiBankCardLine,
    RiLoader4Line,
    RiArrowLeftLine
} from 'react-icons/ri'

const EmployeePayrollReview = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const [employee, setEmployee] = useState(null)
    const [allowances, setAllowances] = useState([])
    const [deductions, setDeductions] = useState([])
    const [overtimes, setOvertimes] = useState([])
    const [cills, setCills] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    // Form States
    const [submitting, setSubmitting] = useState(false)
    const [allowanceForm, setAllowanceForm] = useState({ title: '', amount: '' })
    const [deductionForm, setDeductionForm] = useState({
        title: '',
        deduction_type: 'one_time',
        amount: '',
        monthly_deduction: '',
        month: new Date().toISOString().split('T')[0],
        start_date: '',
        end_date: '',
        is_active: true
    })
    const [overtimeForm, setOvertimeForm] = useState({
        month: new Date().toISOString().split('T')[0],
        hours_1_5: '',
        hours_2_0: ''
    })
    const [cillForm, setCillForm] = useState({
        days_sold: '',
        month: new Date().toISOString().split('T')[0]
    })

    const [isEditingAllowance, setIsEditingAllowance] = useState(false)
    const [editingAllowanceId, setEditingAllowanceId] = useState(null)
    const [isEditingDeduction, setIsEditingDeduction] = useState(false)
    const [editingDeductionId, setEditingDeductionId] = useState(null)
    const [isEditingOvertime, setIsEditingOvertime] = useState(false)
    const [editingOvertimeId, setEditingOvertimeId] = useState(null)
    const [isEditingCill, setIsEditingCill] = useState(false)
    const [editingCillId, setEditingCillId] = useState(null)

    const fetchData = useCallback(async () => {
        try {
            setLoading(true)
            const token = Cookies.get('token')
            const headers = { 'Authorization': `Bearer ${token}` }

            const [empRes, allowRes, dedRes, overRes, cillRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/v1/employees/${id}/`, { headers }),
                axios.get(`${API_BASE_URL}/api/v1/payroll/employee/${id}/allowances/`, { headers }),
                axios.get(`${API_BASE_URL}/api/v1/payroll/employee/${id}/deductions/`, { headers }),
                axios.get(`${API_BASE_URL}/api/v1/payroll/employee/${id}/overtimes/`, { headers }),
                axios.get(`${API_BASE_URL}/api/v1/payroll/employees/${id}/cills/`, { headers })
            ])

            setEmployee(empRes.data)
            setAllowances(allowRes.data)
            setDeductions(dedRes.data)
            console.log(overRes.data)
            setOvertimes(overRes.data)
            setCills(cillRes.data)
            setError(null)
        } catch (err) {
            console.error("Error fetching payroll data:", err)
            setError("Failed to load employee payroll details.")
        } finally {
            setLoading(false)
        }
    }, [id])

    useEffect(() => {
        if (id) fetchData()
    }, [id, fetchData])

    // Allowance Handlers
    const handleAllowanceSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        try {
            const token = Cookies.get('token')
            const headers = { 'Authorization': `Bearer ${token}` }
            const payload = {
                ...allowanceForm,
                month: new Date().toISOString().split('T')[0],
                employee: id
            }
            if (isEditingAllowance) {
                await axios.patch(`${API_BASE_URL}/api/v1/payroll/employee/allowances/${editingAllowanceId}/`, payload, { headers })
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Allowance updated successfully',
                    timer: 2000,
                    showConfirmButton: false
                })
            } else {
                await axios.post(`${API_BASE_URL}/api/v1/payroll/employee/${id}/allowances/`, payload, { headers })
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Allowance added successfully',
                    timer: 2000,
                    showConfirmButton: false
                })
            }
            setAllowanceForm({ title: '', amount: '' })
            setIsEditingAllowance(false)
            setEditingAllowanceId(null)
            document.querySelector('#allowanceModal .btn-close').click()
            fetchData()
        } catch (err) {
            Swal.fire('Error', 'Failed to save allowance', 'error')
        } finally {
            setSubmitting(false)
        }
    }

    const handleDeleteAllowance = async (allowId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!'
        })
        if (result.isConfirmed) {
            try {
                const token = Cookies.get('token')
                await axios.delete(`${API_BASE_URL}/api/v1/payroll/employee/allowances/${allowId}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Allowance removed.',
                    timer: 2000,
                    showConfirmButton: false
                })
                fetchData()
            } catch (err) {
                Swal.fire('Error', 'Failed to delete.', 'error')
            }
        }
    }

    // Deduction Handlers
    const handleDeductionSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        try {
            const token = Cookies.get('token')
            const headers = { 'Authorization': `Bearer ${token}` }

            // Prepare payload according to model requirements
            const payload = {
                ...deductionForm,
                employee: id,
                amount: deductionForm.deduction_type === 'one_time' ? deductionForm.amount : null,
                month: deductionForm.deduction_type === 'one_time' ? deductionForm.month : null,
                start_date: deductionForm.deduction_type === 'recurring' ? deductionForm.start_date : null,
                end_date: (deductionForm.deduction_type === 'recurring' && deductionForm.end_date) ? deductionForm.end_date : null,
            }

            if (isEditingDeduction) {
                await axios.patch(`${API_BASE_URL}/api/v1/payroll/employee/deductions/${editingDeductionId}/`, payload, { headers })
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Deduction updated successfully',
                    timer: 2000,
                    showConfirmButton: false
                })
            } else {
                await axios.post(`${API_BASE_URL}/api/v1/payroll/employee/${id}/deductions/`, payload, { headers })
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Deduction added successfully',
                    timer: 1000,
                    showConfirmButton: false
                })
            }

            setDeductionForm({
                title: '',
                deduction_type: 'one_time',
                amount: '',
                monthly_deduction: '',
                month: new Date().toISOString().split('T')[0],
                start_date: '',
                end_date: '',
                is_active: true
            })
            setIsEditingDeduction(false)
            setEditingDeductionId(null)
            document.querySelector('#deductionModal .btn-close').click()
            fetchData()
        } catch (err) {
            console.error("Deduction save error:", err.response?.data)
            Swal.fire('Error', err.response?.data?.error || 'Failed to save deduction. Check console for details.', 'error')
        } finally {
            setSubmitting(false)
        }
    }

    const handleDeleteDeduction = async (dedId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!'
        })
        if (result.isConfirmed) {
            try {
                const token = Cookies.get('token')
                await axios.delete(`${API_BASE_URL}/api/v1/payroll/employee/deductions/${dedId}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Deduction removed.',
                    timer: 2000,
                    showConfirmButton: false
                })
                fetchData()
            } catch (err) {
                Swal.fire('Error', 'Failed to delete.', 'error')
            }
        }
    }

    // Overtime Handlers
    const handleOvertimeSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        try {
            const token = Cookies.get('token')
            const headers = { 'Authorization': `Bearer ${token}` }
            const payload = {
                ...overtimeForm,
                month: new Date().toISOString().split('T')[0],
                employee: id
            }
            if (isEditingOvertime) {
                await axios.patch(`${API_BASE_URL}/api/v1/payroll/employee/overtimes/${editingOvertimeId}/`, payload, { headers })
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Overtime updated successfully',
                    timer: 2000,
                    showConfirmButton: false
                })
            } else {
                await axios.post(`${API_BASE_URL}/api/v1/payroll/employee/${id}/overtimes/`, payload, { headers })
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Overtime added successfully',
                    timer: 2000,
                    showConfirmButton: false
                })
            }
            setOvertimeForm({ month: new Date().toISOString().split('T')[0], hours_1_5: '', hours_2_0: '' })
            setIsEditingOvertime(false)
            setEditingOvertimeId(null)
            document.querySelector('#overtimeModal .btn-close').click()
            fetchData()
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || 'Failed to save overtime', 'error')
        } finally {
            setSubmitting(false)
        }
    }

    const handleDeleteOvertime = async (ovId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!'
        })
        if (result.isConfirmed) {
            try {
                const token = Cookies.get('token')
                await axios.delete(`${API_BASE_URL}/api/v1/payroll/employee/overtimes/${ovId}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Overtime removed.',
                    timer: 2000,
                    showConfirmButton: false
                })
                fetchData()
            } catch (err) {
                Swal.fire('Error', 'Failed to delete.', 'error')
            }
        }
    }

    // CILL Handlers
    const handleCillSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        try {
            const token = Cookies.get('token')
            const headers = { 'Authorization': `Bearer ${token}` }
            const payload = {
                ...cillForm,
                employee: id
            }
            if (isEditingCill) {
                await axios.patch(`${API_BASE_URL}/api/v1/payroll/employees/cills/${editingCillId}/`, payload, { headers })
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Leave sale updated successfully',
                    timer: 2000,
                    showConfirmButton: false
                })
            } else {
                await axios.post(`${API_BASE_URL}/api/v1/payroll/employees/${id}/cills/`, payload, { headers })
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Leave sale added successfully',
                    timer: 2000,
                    showConfirmButton: false
                })
            }

            setCillForm({ days_sold: '', month: new Date().toISOString().split('T')[0] })
            setIsEditingCill(false)
            setEditingCillId(null)
            document.querySelector('#cillModal .btn-close').click()
            fetchData()
        } catch (err) {
            Swal.fire('Error', err.response?.data?.error || 'Failed to save Leave Sale', 'error')
        } finally {
            setSubmitting(false)
        }
    }

    const handleDeleteCill = async (cillId) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!'
        })
        if (result.isConfirmed) {
            try {
                const token = Cookies.get('token')
                await axios.delete(`${API_BASE_URL}/api/v1/payroll/employees/cills/${cillId}/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted!',
                    text: 'Leave sale removed.',
                    timer: 2000,
                    showConfirmButton: false
                })
                fetchData()
            } catch (err) {
                Swal.fire('Error', 'Failed to delete.', 'error')
            }
        }
    }

    // Calculations
    const activeContract = employee?.contracts?.find(c => c.is_active)
    const basicSalary = parseFloat(employee?.basic_salary || 0)
    const totalAllowances = allowances.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
    const totalOvertimes_1_5 = overtimes.reduce((sum, item) => sum + parseFloat(item.amount_1_5 || 0), 0)
    const totalOvertimes_2_0 = overtimes.reduce((sum, item) => sum + parseFloat(item.amount_2_0 || 0), 0)
    const totalCills = cills.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0)
    const totalEarnings = basicSalary + totalAllowances + totalOvertimes_1_5 + totalOvertimes_2_0 + totalCills

    const totalDeductions = deductions.reduce((sum, item) => {
        if (!item.is_active) return sum
        return sum + parseFloat(item.monthly_deduction || 0)
    }, 0)
    const netPay = totalEarnings - totalDeductions

    if (loading) {
        return (
            <div>
                <Topbar />
                <Sidebar />
                <div className="app-content-area">
                    <div className="app-content-wrap">
                        <div className="container-fluid d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                            <RiLoader4Line className="spinner-border text-primary fs-1 mb-3" style={{ border: 'none', animation: 'spin 1s linear infinite' }} />
                            <p className="text-muted">Loading payroll review...</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div>
                <Topbar />
                <Sidebar />
                <div className="app-content-area">
                    <div className="app-content-wrap">
                        <div className="container-fluid d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                            <p className="text-danger fw-bold">{error}</p>
                            <button onClick={fetchData} className="btn btn-primary mt-2">Try Again</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div>
            <Topbar />
            <Sidebar />

            <div className="app-content-area">
                <div className="app-content-wrap">
                    <div className="container-fluid">
                        <div className="row justify-content-center">
                            <div className="col-xl-12">
                                <div className="card border-0 shadow-lg">
                                    <div className="card-body p-5">
                                        <div className="payslip-wrapper position-relative">
                                            <div className="watermark opacity-25" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(-45deg)', fontSize: '100px', fontWeight: 'bold', color: '#f0f0f0', pointerEvents: 'none', zIndex: 0 }}>
                                                PAYROLL REVIEW
                                            </div>



                                            <div className="d-flex justify-content-between align-items-center mb-5" style={{ position: 'relative', zIndex: 1 }}>
                                                <div>
                                                    <h2 className="fw-bold mb-0">Payroll Review</h2>
                                                    <p className="text-muted">Previewing payroll for {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
                                                </div>
                                                <div className="text-end">
                                                    <div className="mb-4" style={{ position: 'relative', zIndex: 1 }}>
                                                        <button className="btn btn-outline-primary d-flex align-items-center gap-2" onClick={() => navigate('/hr/payroll/run')}>
                                                            <RiArrowLeftLine /> Back
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Employee Information */}
                                            <div className="row g-4 mb-5" style={{ position: 'relative', zIndex: 1 }}>
                                                <div className="col-md-6">
                                                    <div className="card bg-light-subtle border-0">
                                                        <div className="card-body">
                                                            <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                                                                <RiMoneyDollarCircleLine className="text-primary" /> Employee Details
                                                            </h5>
                                                            <div className="d-flex flex-column gap-2">
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="text-muted">Employee ID:</span>
                                                                    <span className="fw-bold">{employee.employee_id}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="text-muted">Name:</span>
                                                                    <span className="fw-bold">{employee.first_name} {employee.last_name}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="text-muted">Department:</span>
                                                                    <span className="fw-bold">{employee.department?.name || 'N/A'}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="text-muted">Position:</span>
                                                                    <span className="fw-bold">{employee.position || 'N/A'}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="card bg-light-subtle border-0">
                                                        <div className="card-body">
                                                            <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                                                                <RiBankCardLine className="text-success" /> Payment Information
                                                            </h5>
                                                            <div className="d-flex flex-column gap-2">
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="text-muted">Bank Name:</span>
                                                                    <span className="fw-bold">{employee.bank_name || 'N/A'}</span>
                                                                </div>

                                                                <div className="d-flex justify-content-between">
                                                                    <span className="text-muted">Branch</span>
                                                                    <span className="fw-bold">{employee.bank_branch || "N/A"}</span>
                                                                </div>
                                                                <div className="d-flex justify-content-between">
                                                                    <span className="text-muted">Account Number:</span>
                                                                    <span className="fw-bold">{employee.bank_account_number || 'N/A'}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Earnings & Deductions */}
                                            <div className="row g-4 mb-5" style={{ position: 'relative', zIndex: 1 }}>
                                                <div className="col-md-6">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <h5 className="fw-bold mb-0">Earnings</h5>
                                                        <div className="d-flex gap-2">
                                                            <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1" data-bs-toggle="modal" data-bs-target="#allowanceModal" onClick={() => { setIsEditingAllowance(false); setAllowanceForm({ title: '', amount: '' }); }}>
                                                                <RiAddLine /> Allowance
                                                            </button>
                                                            <button className="btn btn-sm btn-outline-info d-flex align-items-center gap-1" data-bs-toggle="modal" data-bs-target="#overtimeModal" onClick={() => { setIsEditingOvertime(false); setOvertimeForm({ month: new Date().toISOString().split('T')[0], hours_1_5: '', hours_2_0: '' }); }}>
                                                                <RiTimeLine /> Overtime
                                                            </button>
                                                            <button className="btn btn-sm btn-outline-success d-flex align-items-center gap-1" data-bs-toggle="modal" data-bs-target="#cillModal" onClick={() => { setIsEditingCill(false); setCillForm({ days_sold: '', month: new Date().toISOString().split('T')[0] }); }}>
                                                                <RiMoneyDollarCircleLine /> Sold Leave
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <table className="table table-hover border">
                                                        <thead className="bg-light-subtle">
                                                            <tr>
                                                                <th>Description</th>
                                                                <th className="text-end">Amount</th>
                                                                <th className="text-end">Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td>Basic Salary</td>
                                                                <td className="text-end fw-bold">${basicSalary.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                                                <td></td>
                                                            </tr>
                                                            {allowances.map(item => (
                                                                <tr key={item.id}>
                                                                    <td>{item.title} <small className="text-muted">(Allowance)</small></td>
                                                                    <td className="text-end">${parseFloat(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                                                    <td className="text-end">
                                                                        <div className="d-flex justify-content-end gap-1">
                                                                            <button className="btn btn-sm btn-link text-info p-0" data-bs-toggle="modal" data-bs-target="#allowanceModal" onClick={() => { setIsEditingAllowance(true); setEditingAllowanceId(item.id); setAllowanceForm({ title: item.title, amount: item.amount }); }}>
                                                                                <RiEdit2Line />
                                                                            </button>
                                                                            <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleDeleteAllowance(item.id)}>
                                                                                <RiDeleteBinLine />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                            {overtimes.map(item => (
                                                                <>
                                                                    <tr key={item.id}>
                                                                        <td>Overtime <small className="text-muted">(1.5x)</small></td>
                                                                        <td className="text-end">${parseFloat(item.amount_1_5).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>                                                                    <td className="text-end">
                                                                            <div className="d-flex justify-content-end gap-1">
                                                                                <button className="btn btn-sm btn-link text-info p-0" data-bs-toggle="modal" data-bs-target="#overtimeModal" onClick={() => { setIsEditingOvertime(true); setEditingOvertimeId(item.id); setOvertimeForm({ month: item.month, hours_1_5: item.hours_1_5, hours_2_0: item.hours_2_0 }); }}>
                                                                                    <RiEdit2Line />
                                                                                </button>
                                                                                <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleDeleteOvertime(item.id)}>
                                                                                    <RiDeleteBinLine />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                    <tr key={item.id}>
                                                                        <td>Overtime <small className="text-muted">(2.0x)</small></td>
                                                                        <td className="text-end">${parseFloat(item.amount_2_0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>                                                                    <td className="text-end">

                                                                        </td>
                                                                    </tr> </>
                                                            ))}
                                                            {cills.map(item => (
                                                                <tr key={item.id}>
                                                                    <td>CILL <small className="text-muted">({item.days_sold} days)</small></td>
                                                                    <td className="text-end">${parseFloat(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                                                    <td className="text-end">
                                                                        <div className="d-flex justify-content-end gap-1">
                                                                            <button className="btn btn-sm btn-link text-info p-0" data-bs-toggle="modal" data-bs-target="#cillModal" onClick={() => { setIsEditingCill(true); setEditingCillId(item.id); setCillForm({ days_sold: item.days_sold, month: item.month }); }}>
                                                                                <RiEdit2Line />
                                                                            </button>
                                                                            <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleDeleteCill(item.id)}>
                                                                                <RiDeleteBinLine />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                        <tfoot className="bg-light-subtle fw-bold">
                                                            <tr>
                                                                <td>Total Earnings</td>
                                                                <td className="text-end text-primary">${totalEarnings.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                                                <td></td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                                        <h5 className="fw-bold mb-0">Deductions</h5>
                                                        <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1" data-bs-toggle="modal" data-bs-target="#deductionModal" onClick={() => {
                                                            setIsEditingDeduction(false);
                                                            setDeductionForm({
                                                                title: '',
                                                                deduction_type: 'one_time',
                                                                amount: '',
                                                                monthly_deduction: '',
                                                                month: new Date().toISOString().split('T')[0],
                                                                start_date: '',
                                                                end_date: '',
                                                                is_active: true
                                                            });
                                                        }}>
                                                            <RiAddLine /> Deduction
                                                        </button>
                                                    </div>
                                                    <table className="table table-hover border">
                                                        <thead className="bg-light-subtle">
                                                            <tr>
                                                                <th>Description</th>
                                                                <th className="text-end">Amount</th>
                                                                <th className="text-end">Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {deductions.length === 0 ? (
                                                                <tr>
                                                                    <td colSpan="3" className="text-center text-muted py-3">No deductions added</td>
                                                                </tr>
                                                            ) : (
                                                                deductions.map(item => (
                                                                    <tr key={item.id} className={!item.is_active ? 'opacity-50' : ''}>
                                                                        <td>
                                                                            <div className="fw-bold">{item.title}</div>
                                                                            <small className="text-muted">
                                                                                {item.deduction_type === 'one_time' ? (
                                                                                    `One-time: ${item.month}`
                                                                                ) : (
                                                                                    `Recurring: ${item.start_date}${item.end_date ? ` to ${item.end_date}` : ' (Ongoing)'}`
                                                                                )}
                                                                            </small>
                                                                            {!item.is_active && <span className="ms-2 badge bg-secondary">Inactive</span>}
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <div>${parseFloat(item.monthly_deduction).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                                                                            {item.deduction_type === 'one_time' && (
                                                                                <small className="text-muted">Total: ${parseFloat(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</small>
                                                                            )}
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <div className="d-flex justify-content-end gap-1">
                                                                                <button className="btn btn-sm btn-link text-info p-0" data-bs-toggle="modal" data-bs-target="#deductionModal" onClick={() => {
                                                                                    setIsEditingDeduction(true);
                                                                                    setEditingDeductionId(item.id);
                                                                                    setDeductionForm({
                                                                                        title: item.title,
                                                                                        deduction_type: item.deduction_type,
                                                                                        amount: item.amount || '',
                                                                                        monthly_deduction: item.monthly_deduction,
                                                                                        month: item.month || new Date().toISOString().split('T')[0],
                                                                                        start_date: item.start_date || '',
                                                                                        end_date: item.end_date || '',
                                                                                        is_active: item.is_active
                                                                                    });
                                                                                }}>
                                                                                    <RiEdit2Line />
                                                                                </button>
                                                                                <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleDeleteDeduction(item.id)}>
                                                                                    <RiDeleteBinLine />
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            )}
                                                        </tbody>
                                                        <tfoot className="bg-light-subtle fw-bold">
                                                            <tr>
                                                                <td>Total Deductions</td>
                                                                <td className="text-end text-danger">${totalDeductions.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                                                                <td></td>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Allowance Modal */}
            <div className="modal fade" id="allowanceModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow">
                        <div className="modal-header bg-primary text-white">
                            <h5 className="modal-title">{isEditingAllowance ? 'Edit Allowance' : 'Add Allowance'}</h5>
                            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"></button>
                        </div>
                        <form onSubmit={handleAllowanceSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Title</label>
                                    <input type="text" className="form-control" value={allowanceForm.title} onChange={(e) => setAllowanceForm({ ...allowanceForm, title: e.target.value })} required />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Amount</label>
                                    <input type="number" step="0.01" className="form-control" value={allowanceForm.amount} onChange={(e) => setAllowanceForm({ ...allowanceForm, amount: e.target.value })} required />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                                <button type="submit" className="btn btn-primary" disabled={submitting}>
                                    {submitting ? 'Saving...' : (isEditingAllowance ? 'Update Allowance' : 'Add Allowance')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Deduction Modal */}
            <div className="modal fade" id="deductionModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow">
                        <div className="modal-header bg-danger text-white">
                            <h5 className="modal-title">{isEditingDeduction ? 'Edit Deduction' : 'Add Deduction'}</h5>
                            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"></button>
                        </div>
                        <form onSubmit={handleDeductionSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Deduction Title</label>
                                    <input type="text" className="form-control" value={deductionForm.title} onChange={(e) => setDeductionForm({ ...deductionForm, title: e.target.value })} required placeholder="e.g. Salary Advance, Loan" />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Deduction Type</label>
                                    <select className="form-select" value={deductionForm.deduction_type} onChange={(e) => setDeductionForm({ ...deductionForm, deduction_type: e.target.value })}>
                                        <option value="one_time">One-Time Deduction</option>
                                        <option value="recurring">Recurring Monthly Deduction</option>
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Monthly Deduction Amount</label>
                                    <input type="number" step="0.01" className="form-control" value={deductionForm.monthly_deduction} onChange={(e) => setDeductionForm({ ...deductionForm, monthly_deduction: e.target.value })} required placeholder="Amount to deduct each month" />
                                </div>

                                {deductionForm.deduction_type === 'one_time' ? (
                                    <>
                                        <div className="mb-3">
                                            <label className="form-label">Total Amount</label>
                                            <input type="number" step="0.01" className="form-control" value={deductionForm.amount} onChange={(e) => setDeductionForm({ ...deductionForm, amount: e.target.value })} required placeholder="Total amount to be paid off" />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Target Month</label>
                                            <input type="date" className="form-control" value={deductionForm.month} onChange={(e) => setDeductionForm({ ...deductionForm, month: e.target.value })} required />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="row">
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">Start Date</label>
                                                <input type="date" className="form-control" value={deductionForm.start_date} onChange={(e) => setDeductionForm({ ...deductionForm, start_date: e.target.value })} required />
                                            </div>
                                            <div className="col-md-6 mb-3">
                                                <label className="form-label">End Date (Optional)</label>
                                                <input type="date" className="form-control" value={deductionForm.end_date} onChange={(e) => setDeductionForm({ ...deductionForm, end_date: e.target.value })} />
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="form-check mb-3">
                                    <input className="form-check-input" type="checkbox" id="isActiveDeduction" checked={deductionForm.is_active} onChange={(e) => setDeductionForm({ ...deductionForm, is_active: e.target.checked })} />
                                    <label className="form-check-label" htmlFor="isActiveDeduction">
                                        Is Active
                                    </label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                                <button type="submit" className="btn btn-danger" disabled={submitting}>
                                    {submitting ? 'Saving...' : (isEditingDeduction ? 'Update Deduction' : 'Add Deduction')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Overtime Modal */}
            <div className="modal fade" id="overtimeModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow">
                        <div className="modal-header bg-info text-white">
                            <h5 className="modal-title">{isEditingOvertime ? 'Edit Overtime' : 'Add Overtime'}</h5>
                            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"></button>
                        </div>
                        <form onSubmit={handleOvertimeSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Month</label>
                                    <input type="date" className="form-control" value={overtimeForm.month} onChange={(e) => setOvertimeForm({ ...overtimeForm, month: e.target.value })} required />
                                </div>
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Hours (1.5x)</label>
                                        <input type="number" step="0.5" className="form-control" value={overtimeForm.hours_1_5} onChange={(e) => setOvertimeForm({ ...overtimeForm, hours_1_5: e.target.value })} placeholder="e.g. 2.0" />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Hours (2.0x)</label>
                                        <input type="number" step="0.5" className="form-control" value={overtimeForm.hours_2_0} onChange={(e) => setOvertimeForm({ ...overtimeForm, hours_2_0: e.target.value })} placeholder="e.g. 1.0" />
                                    </div>
                                </div>
                                <div className="bg-light-subtle p-3 rounded">
                                    <small className="text-muted">Overtime will be calculated automatically based on the employee's hourly rate.</small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                                <button type="submit" className="btn btn-info text-white" disabled={submitting}>
                                    {submitting ? 'Saving...' : (isEditingOvertime ? 'Update Overtime' : 'Add Overtime')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="cillModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow">
                        <div className="modal-header bg-success text-white">
                            <h5 className="modal-title">{isEditingCill ? 'Edit Leave Sale' : 'Sell Leave Days'}</h5>
                            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"></button>
                        </div>
                        <form onSubmit={handleCillSubmit}>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Month</label>
                                    <input type="date" className="form-control" value={cillForm.month} onChange={(e) => setCillForm({ ...cillForm, month: e.target.value })} required />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Number of Days to Sell</label>
                                    <input type="number" step="0.5" className="form-control" value={cillForm.days_sold} onChange={(e) => setCillForm({ ...cillForm, days_sold: e.target.value })} required placeholder="e.g. 5.0" />
                                    {employee?.vacation_leave_days_available !== undefined && (
                                        <div className="form-text text-muted">
                                            Available Leave Balance: <strong>{employee.vacation_leave_days_available} days</strong>
                                        </div>
                                    )}
                                </div>
                                <div className="bg-light-subtle p-3 rounded">
                                    <small className="text-muted">Currently Selling: <strong>{cillForm.days_sold || 0} days</strong></small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-light" data-bs-dismiss="modal">Cancel</button>
                                <button type="submit" className="btn btn-success" disabled={submitting}>
                                    {submitting ? 'Saving...' : (isEditingCill ? 'Update Sale' : 'Sell Leave')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .card { transition: all 0.3s ease; }
                .table-hover tbody tr:hover { background-color: rgba(248, 249, 250, 1); }
                .badge { font-weight: 500; }
                .watermark { opacity: 0.05 !important; }
            `}</style>
        </div>
    )
}

export default EmployeePayrollReview
